<script>
/**
 * 상품 상세 페이지.
 *
 * 기능:
 * - 상품 상세 조회 (productCode로 API 호출)
 * - 수정: 편집 모드 진입 후 수정 저장
 * - 삭제: 확인 다이얼로그 후 soft delete → 목록 이동
 * - view_yn 토글: 상세 페이지에서도 즉시 토글 가능
 *
 * 향후 확장 예정 (탭 영역 placeholder):
 * - 구매내역 탭 (payment 연결)
 * - 리뷰 관리 탭 (product_review 연결)
 */
import { getProductDetail, updateProduct, deleteProduct, toggleProductView, uploadProductImage, deleteProductImage } from '@/api/product.js'
import { getCategoryList } from '@/api/category.js'
import { getAdminInfo } from '@/utils/token.js'

/** 탭 목록 정의 (향후 확장 시 items에 추가) */
const TABS = [
  { key: 'info', label: '상품 정보' },
  // 향후 추가 예정
  // { key: 'purchase', label: '구매내역' },
  // { key: 'review', label: '리뷰 관리' },
]

export default {
  name: 'ProductDetailPage',

  data() {
    return {
      /** 현재 조회 중인 상품 데이터 */
      product: null,

      /** 편집 모드 여부 */
      editMode: false,

      /** 편집 폼 데이터 */
      editForm: {
        productName: '',
        categoryCode: '',
        productText: '',
        currency: '',
        conventionCost: null,
        weight: null,
        orderby: null,
      },

      /** 카테고리 선택 옵션 (수정 폼에서 사용) */
      categoryOptions: [{ title: '카테고리 없음', value: '' }],

      /** 활성 탭 키 */
      activeTab: 'info',

      /** 탭 목록 */
      tabs: TABS,

      /* ── UI 상태 ── */
      loading: false,
      saving: false,
      deleting: false,
      toggling: false,
      uploading: false,

      /** 삭제 확인 다이얼로그 */
      deleteDialog: false,

      /** 오류 메시지 */
      errorMsg: '',
      saveErrorMsg: '',

      /** 로그인한 관리자 정보 */
      adminInfo: {},
    }
  },

  computed: {
    /** 현재 조회할 상품코드 (라우트 파라미터) */
    productCode() {
      return this.$route.params.productCode
    },
    /** HQ 관리자 여부: 수정/삭제/토글 버튼 노출 제어 */
    isHq() {
      return this.adminInfo.adminType === 'HQ'
    },
  },

  mounted() {
    this.adminInfo = getAdminInfo()
    Promise.all([this.fetchDetail(), this.fetchCategories()])
  },

  methods: {
    extractApiErrorMessage(error, fallback) {
      const data = error?.response?.data
      if (typeof data?.message === 'string' && data.message.trim()) return data.message
      if (typeof data?.error === 'string' && data.error.trim()) return data.error
      if (Array.isArray(data?.errors) && data.errors.length) {
        const first = data.errors[0]
        if (typeof first?.defaultMessage === 'string' && first.defaultMessage.trim()) return first.defaultMessage
        if (typeof first?.message === 'string' && first.message.trim()) return first.message
      }
      return fallback
    },

    /** 상품 상세 데이터를 서버에서 로딩한다 */
    async fetchDetail() {
      this.loading = true
      this.errorMsg = ''
      try {
        const res = await getProductDetail(this.productCode)
        if (res.data?.code === 0) {
          this.product = res.data
        } else {
          this.errorMsg = res.data?.message || '상품 정보를 불러올 수 없습니다.'
        }
      } catch (e) {
        console.error('[ProductDetailPage] fetchDetail error:', e)
        this.errorMsg = '상품 정보를 불러오는 중 오류가 발생했습니다.'
      } finally {
        this.loading = false
      }
    },

    /**
     * 카테고리 전체 목록을 로딩한다 (수정 폼 카테고리 select용).
     * 페이지당 100건으로 설정하여 대부분의 경우 전체를 한 번에 가져온다.
     */
    async fetchCategories() {
      try {
        const res = await getCategoryList({ page: 1, size: 100 })
        if (res.data?.code === 0) {
          const list = res.data.data?.items || []
          this.categoryOptions = [
            { title: '카테고리 없음', value: '' },
            ...list.map(c => ({ title: c.categoryName, value: c.categoryCode })),
          ]
        }
      } catch (e) {
        console.error('[ProductDetailPage] fetchCategories error:', e)
      }
    },

    /** 편집 모드 진입: 현재 상품 데이터를 폼에 복사 */
    enterEditMode() {
      this.editForm = {
        productName:    this.product.productName    || '',
        categoryCode:   this.product.categoryCode   || '',
        productText:    this.product.productText    || '',
        currency:       this.product.currency       || '',
        conventionCost: this.product.conventionCost ?? null,
        weight:         this.product.weight ?? null,
        orderby:
          this.product.orderby === null || this.product.orderby === undefined || this.product.orderby === ''
            ? null
            : Number(this.product.orderby),
      }
      this.saveErrorMsg = ''
      this.editMode = true
    },

    /** 편집 취소: 폼 초기화 후 편집 모드 종료 */
    cancelEdit() {
      this.editMode = false
      this.saveErrorMsg = ''
    },

    /** 수정 저장 */
    async handleSave() {
      this.saveErrorMsg = ''
      if (!this.editForm.productName?.trim()) {
        this.saveErrorMsg = '상품명을 입력해 주세요.'
        return
      }
      if (this.editForm.orderby !== null && this.editForm.orderby !== '' && Number(this.editForm.orderby) < 0) {
        this.saveErrorMsg = '정렬순서는 0 이상이어야 합니다.'
        return
      }

      this.saving = true
      try {
        const res = await updateProduct(this.productCode, {
          productName:    this.editForm.productName.trim(),
          categoryCode:   this.editForm.categoryCode || null,
          productText:    this.editForm.productText  || null,
          currency:       this.editForm.currency || null,
          conventionCost: this.editForm.conventionCost ?? null,
          weight:         this.editForm.weight === '' || this.editForm.weight === null || this.editForm.weight === undefined
            ? null
            : Number(this.editForm.weight),
          orderby:
            this.editForm.orderby === '' || this.editForm.orderby === null || this.editForm.orderby === undefined
              ? null
              : Number(this.editForm.orderby),
        })
        if (res.data?.code === 0) {
          // 수정 성공: 화면 데이터 갱신 후 편집 모드 종료
          this.product = res.data
          this.editMode = false
        } else {
          this.saveErrorMsg = res.data?.message || '수정 중 오류가 발생했습니다.'
        }
      } catch (e) {
        console.error('[ProductDetailPage] handleSave error:', e)
        this.saveErrorMsg = this.extractApiErrorMessage(e, '수정 중 오류가 발생했습니다.')
      } finally {
        this.saving = false
      }
    },

    /** 삭제 실행 */
    async handleDelete() {
      this.deleting = true
      try {
        const res = await deleteProduct(this.productCode)
        if (res.data?.code === 0) {
          // 삭제 성공: 상품 목록으로 이동
          this.$router.push('/products')
        } else {
          this.deleteDialog = false
          this.errorMsg = res.data?.message || '삭제 중 오류가 발생했습니다.'
        }
      } catch (e) {
        console.error('[ProductDetailPage] handleDelete error:', e)
        this.deleteDialog = false
        this.errorMsg = '삭제 중 오류가 발생했습니다.'
      } finally {
        this.deleting = false
      }
    },

    /** 이미지 업로드 */
    async handleImageUpload(fileType, file) {
      if (!file || this.uploading) return
      this.uploading = true
      try {
        const res = await uploadProductImage(this.productCode, fileType, file)
        if (res.data?.code === 0) {
          this.product = res.data
        }
      } catch (e) {
        console.error('[ProductDetailPage] handleImageUpload error:', e)
      } finally {
        this.uploading = false
      }
    },

    /** 이미지 삭제 */
    async handleImageDelete(seq) {
      try {
        const res = await deleteProductImage(this.productCode, seq)
        if (res.data?.code === 0) {
          this.product = res.data
        }
      } catch (e) {
        console.error('[ProductDetailPage] handleImageDelete error:', e)
      }
    },

    /** view_yn 토글 */
    async handleToggleView() {
      if (this.toggling) return
      this.toggling = true
      try {
        const res = await toggleProductView(this.productCode)
        if (res.data?.code === 0) {
          this.product = { ...this.product, viewYn: res.data.viewYn }
        }
      } catch (e) {
        console.error('[ProductDetailPage] handleToggleView error:', e)
      } finally {
        this.toggling = false
      }
    },

    /** 날짜 포맷: YYYY-MM-DD HH:mm */
    formatDateTime(dt) {
      return dt ? dt.substring(0, 16).replace('T', ' ') : '-'
    },

    /** 중량 표시 */
    formatWeight(val) {
      if (val === null || val === undefined || val === '') return '-'
      const n = Number(val)
      return Number.isNaN(n)
        ? '-'
        : n.toLocaleString('ko-KR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    },

    /** 목록으로 돌아가기 */
    goBack() {
      this.$router.push('/products')
    },
  },
}
</script>

<template>
  <div>
    <!-- 페이지 헤더 -->
    <div class="d-flex align-center justify-space-between mb-5">
      <div>
        <div class="d-flex align-center ga-1 text-caption text-medium-emphasis mb-1">
          <span class="text-primary breadcrumb-link" @click="$router.push('/products')">상품관리</span>
          <v-icon size="12">mdi-chevron-right</v-icon>
          <span>상품 상세</span>
        </div>
        <div class="text-h5 font-weight-bold">상품 상세</div>
        <div class="text-body-2 text-grey mt-1">상품 정보 조회 및 수정</div>
      </div>

      <!-- HQ 전용 액션 버튼 영역 -->
      <div v-if="isHq && product" class="d-flex ga-2">
        <template v-if="!editMode">
          <v-btn
            variant="tonal"
            color="primary"
            rounded="lg"
            prepend-icon="mdi-pencil-outline"
            @click="enterEditMode"
          >
            수정
          </v-btn>
          <v-btn
            variant="tonal"
            color="error"
            rounded="lg"
            prepend-icon="mdi-delete-outline"
            @click="deleteDialog = true"
          >
            삭제
          </v-btn>
        </template>
        <template v-else>
          <v-btn variant="text" :disabled="saving" @click="cancelEdit">취소</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            rounded="lg"
            :loading="saving"
            prepend-icon="mdi-check"
            @click="handleSave"
          >
            저장
          </v-btn>
        </template>
      </div>
    </div>

    <!-- 로딩 -->
    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

    <!-- 오류 -->
    <v-alert v-if="errorMsg" type="error" variant="tonal" density="compact" class="mb-4">
      {{ errorMsg }}
    </v-alert>

    <template v-if="product">
      <!-- 탭 네비게이션 (향후 구매내역/리뷰 탭 추가 예정) -->
<!--      <v-tabs v-model="activeTab" color="primary" class="mb-1">-->
<!--        <v-tab-->
<!--          v-for="tab in tabs"-->
<!--          :key="tab.key"-->
<!--          :value="tab.key"-->
<!--        >-->
<!--          {{ tab.label }}-->
<!--        </v-tab>-->
<!--      </v-tabs>-->

      <v-window v-model="activeTab">

        <!-- ── 기본 정보 탭 ── -->
        <v-window-item value="info">
          <!-- 수정 오류 -->
          <v-alert
            v-if="saveErrorMsg"
            type="error"
            variant="tonal"
            density="compact"
            rounded="lg"
            class="mb-4"
          >
            {{ saveErrorMsg }}
          </v-alert>

          <!-- 기본 정보 카드 -->
          <v-card rounded="xl" elevation="0" class="detail-card mb-4">
            <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
              <div class="header-icon-wrap bg-primary-soft">
                <v-icon color="primary" size="18">mdi-package-variant</v-icon>
              </div>
              <span class="text-subtitle-2 font-weight-bold">상품 정보</span>
            </div>
            <v-divider />
            <v-card-text class="pa-5">

              <!-- 조회 모드 -->
              <template v-if="!editMode">
                <div class="dl-grid">
                  <div class="dl-item">
                    <div class="dl-label">상품코드</div>
                    <div class="dl-value text-caption text-medium-emphasis">{{ product.productCode }}</div>
                  </div>
                  <div class="dl-item">
                    <div class="dl-label">상품명</div>
                    <div class="dl-value font-weight-bold">{{ product.productName || '-' }}</div>
                  </div>
                  <div class="dl-item">
                    <div class="dl-label">카테고리</div>
                    <div class="dl-value">
                      <v-chip
                        v-if="product.categoryName"
                        size="x-small"
                        color="primary"
                        variant="tonal"
                        label
                        class="font-weight-medium"
                      >
                        {{ product.categoryName }}
                      </v-chip>
                      <span v-else class="text-medium-emphasis">-</span>
                    </div>
                  </div>
                  <div class="dl-item">
                    <div class="dl-label">통화</div>
                    <div class="dl-value text-medium-emphasis">{{ product.currency || '-' }}</div>
                  </div>
                  <div class="dl-item">
                    <div class="dl-label">임가공비</div>
                    <div class="dl-value text-medium-emphasis">{{ product.conventionCost != null ? product.conventionCost.toLocaleString() : '-' }}</div>
                  </div>
                  <div class="dl-item">
                    <div class="dl-label">중량</div>
                    <div class="dl-value text-medium-emphasis">{{ formatWeight(product.weight) }}</div>
                  </div>
                  <div class="dl-item">
                    <div class="dl-label">정렬순서</div>
                    <div class="dl-value text-medium-emphasis">{{ product.orderby ?? '-' }}</div>
                  </div>
                  <div class="dl-item">
                    <div class="dl-label">노출여부</div>
                    <div class="dl-value d-flex align-center ga-2">
                      <v-switch
                        :model-value="product.viewYn === 'Y'"
                        color="primary"
                        density="compact"
                        hide-details
                        :disabled="!isHq || toggling"
                        :loading="toggling"
                        @update:model-value="handleToggleView"
                      />
                      <v-chip
                        :color="product.viewYn === 'Y' ? 'success' : 'grey'"
                        size="x-small"
                        label
                        variant="tonal"
                        class="font-weight-medium"
                      >
                        {{ product.viewYn === 'Y' ? '노출' : '비노출' }}
                      </v-chip>
                    </div>
                  </div>
                  <div class="dl-item">
                    <div class="dl-label">등록일</div>
                    <div class="dl-value text-medium-emphasis">{{ formatDateTime(product.insertDt) }}</div>
                  </div>
                  <div class="dl-item">
                    <div class="dl-label">수정일</div>
                    <div class="dl-value text-medium-emphasis">{{ formatDateTime(product.updateDt) }}</div>
                  </div>
                </div>
                <div class="mt-4 pt-3" style="border-top: 1px solid rgba(0,0,0,0.06);">
                  <div class="dl-label mb-2">상품설명</div>
                  <div class="dl-value" style="white-space: pre-wrap; line-height: 1.8;">
                    {{ product.productText || '등록된 설명이 없습니다.' }}
                  </div>
                </div>
                <!-- 썸네일 이미지 (조회 모드: 보기만) -->
                <div class="mt-4 pt-3" style="border-top: 1px solid rgba(0,0,0,0.06);">
                  <div class="dl-label mb-2">썸네일 이미지</div>
                  <div class="d-flex flex-wrap ga-2 align-center">
                    <div
                      v-for="img in (product.thumbnailImages || [])"
                      :key="img.seq"
                      class="image-item image-view-only"
                    >
                      <v-img :src="img.fileUrl" width="120" height="120" cover class="rounded" />
                    </div>
                    <span v-if="!(product.thumbnailImages || []).length" class="text-caption text-grey">등록된 이미지 없음</span>
                  </div>
                </div>
                <!-- 상세 이미지 (조회 모드: 보기만) -->
                <div class="mt-4 pt-3" style="border-top: 1px solid rgba(0,0,0,0.06);">
                  <div class="dl-label mb-2">상세 이미지</div>
                  <div class="d-flex flex-wrap ga-2 align-center">
                    <div
                      v-for="img in (product.detailImages || [])"
                      :key="img.seq"
                      class="image-item image-view-only"
                    >
                      <v-img :src="img.fileUrl" width="120" height="120" cover class="rounded" />
                    </div>
                    <span v-if="!(product.detailImages || []).length" class="text-caption text-grey">등록된 이미지 없음</span>
                  </div>
                </div>
              </template>

              <!-- 편집 모드 -->
              <template v-else>
                <div class="edit-field-group">
                  <div class="field-label">상품명 *</div>
                  <v-text-field
                    v-model="editForm.productName"
                    variant="outlined"
                    density="comfortable"
                    rounded="lg"
                    maxlength="255"
                    counter
                    hide-details="auto"
                  />
                </div>
                <div class="edit-field-group">
                  <div class="field-label">카테고리</div>
                  <v-select
                    v-model="editForm.categoryCode"
                    :items="categoryOptions"
                    item-title="title"
                    item-value="value"
                    variant="outlined"
                    density="comfortable"
                    rounded="lg"
                    hide-details="auto"
                  />
                </div>
                <div class="edit-field-group">
                  <div class="field-label">통화</div>
                  <v-text-field
                    v-model="editForm.currency"
                    variant="outlined"
                    density="comfortable"
                    rounded="lg"
                    placeholder="예: KRW, USD"
                    hide-details="auto"
                  />
                </div>
                <div class="edit-field-group">
                  <div class="field-label">임가공비</div>
                  <v-text-field
                    v-model.number="editForm.conventionCost"
                    type="number"
                    variant="outlined"
                    density="comfortable"
                    rounded="lg"
                    placeholder="숫자 입력"
                    hide-details="auto"
                  />
                </div>
                <div class="edit-field-group">
                  <div class="field-label">중량</div>
                  <v-text-field
                    v-model="editForm.weight"
                    type="number"
                    step="0.00001"
                    variant="outlined"
                    density="comfortable"
                    rounded="lg"
                    placeholder="예: 7.77500"
                    hide-details="auto"
                  />
                </div>
                <div class="edit-field-group">
                  <div class="field-label">정렬순서</div>
                  <v-text-field
                    v-model.number="editForm.orderby"
                    type="number"
                    min="0"
                    step="1"
                    variant="outlined"
                    density="comfortable"
                    rounded="lg"
                    placeholder="작을수록 먼저 노출 (미입력 시 기본값)"
                    hide-details="auto"
                  />
                </div>
                <div class="edit-field-group d-flex align-center ga-3">
                  <div class="field-label mb-0">노출여부</div>
                  <v-switch
                    :model-value="product.viewYn === 'Y'"
                    color="primary"
                    density="compact"
                    hide-details
                    :loading="toggling"
                    @update:model-value="handleToggleView"
                  />
                </div>
                <div class="edit-field-group">
                  <div class="field-label">상품설명</div>
                  <v-textarea
                    v-model="editForm.productText"
                    variant="outlined"
                    density="comfortable"
                    rounded="lg"
                    rows="6"
                    auto-grow
                    hide-details="auto"
                  />
                </div>
                <!-- 편집 모드에서도 이미지 관리 가능 -->
                <div class="edit-field-group">
                  <div class="field-label">썸네일 이미지</div>
                  <div class="d-flex flex-wrap ga-2 align-center">
                    <div v-for="img in (product.thumbnailImages || [])" :key="img.seq" class="image-item">
                      <v-img :src="img.fileUrl" width="120" height="120" cover class="rounded" />
                      <v-btn icon size="x-small" color="error" variant="tonal" class="delete-btn" @click="handleImageDelete(img.seq)">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </div>
                    <div class="upload-slot">
                      <input ref="thumbInputEdit" type="file" accept="image/*" style="display:none" @change="e => { const f = e.target.files?.[0]; if (f) handleImageUpload('thumbnail', f); e.target.value = '' }">
                      <v-btn variant="tonal" size="small" color="success" :loading="uploading" @click="$refs.thumbInputEdit?.click()">
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>
                <div class="edit-field-group">
                  <div class="field-label">상세 이미지</div>
                  <div class="d-flex flex-wrap ga-2 align-center">
                    <div v-for="img in (product.detailImages || [])" :key="img.seq" class="image-item">
                      <v-img :src="img.fileUrl" width="120" height="120" cover class="rounded" />
                      <v-btn icon size="x-small" color="error" variant="tonal" class="delete-btn" @click="handleImageDelete(img.seq)">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </div>
                    <div class="upload-slot">
                      <input ref="detailInputEdit" type="file" accept="image/*" style="display:none" @change="e => { const f = e.target.files?.[0]; if (f) handleImageUpload('detail', f); e.target.value = '' }">
                      <v-btn variant="tonal" size="small" color="success" :loading="uploading" @click="$refs.detailInputEdit?.click()">
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>
              </template>

            </v-card-text>
          </v-card>
        </v-window-item>

      </v-window>
    </template>

    <!-- 삭제 확인 다이얼로그 -->
    <v-dialog v-model="deleteDialog" max-width="400" persistent>
      <v-card rounded="xl" elevation="4">
        <div class="px-6 pt-5 pb-4 d-flex align-center ga-2">
          <div class="header-icon-wrap bg-error-soft">
            <v-icon color="error" size="18">mdi-alert-circle-outline</v-icon>
          </div>
          <span class="text-subtitle-1 font-weight-bold">상품 삭제 확인</span>
        </div>
        <v-divider />
        <v-card-text class="pa-6">
          <div class="text-body-2 mb-2">
            <strong class="text-error">{{ product?.productName }}</strong> 상품을 삭제하시겠습니까?
          </div>
          <div class="text-caption text-medium-emphasis">
            삭제된 상품은 복구할 수 없습니다.
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="text" :disabled="deleting" @click="deleteDialog = false">취소</v-btn>
          <v-btn
            color="error"
            variant="flat"
            rounded="lg"
            :loading="deleting"
            prepend-icon="mdi-delete-outline"
            @click="handleDelete"
          >
            삭제
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.v-tabs {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

/* ── 공통 카드 ── */
.detail-card {
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}

.detail-card-header {
  background: rgba(0, 0, 0, 0.018);
}

/* ── 헤더 아이콘 ── */
.header-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  flex-shrink: 0;
}
.bg-primary-soft { background: rgba(var(--v-theme-primary), 0.1); }
.bg-error-soft   { background: rgba(var(--v-theme-error), 0.1); }

/* ── 브레드크럼 ── */
.breadcrumb-link {
  cursor: pointer;
}
.breadcrumb-link:hover {
  text-decoration: underline;
}

/* ── 정의 리스트 ── */
.dl-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px 16px;
}

.dl-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.dl-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: rgba(0, 0, 0, 0.38);
  text-transform: uppercase;
}

.dl-value {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.82);
  line-height: 1.4;
}

/* ── 수정 폼 ── */
.edit-field-group {
  margin-bottom: 16px;
}

.field-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.42);
  margin-bottom: 6px;
}

/* ── 이미지 영역 ── */
.image-item {
  position: relative;
}
.image-item .delete-btn {
  position: absolute;
  top: 4px;
  right: 4px;
}
.upload-slot {
  min-width: 80px;
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed rgba(0, 0, 0, 0.2);
  border-radius: 8px;
}
</style>
